import React, { useState } from 'react';
import {
  Search,
  Plus,
  MoreVertical,
  Mail,
  MessageSquare,
  Send,
  Copy,
  Trash2,
  Eye,
  FileText,
  Share2,
  X,
  Bold,
  Italic,
  Underline,
  Link,
  Image,
  List,
  AlignLeft,
  Smile,
} from 'lucide-react';

interface MailItem {
  id: string;
  title: string;
  senderName: string;
  messagePreview: string;
  type: 'mail' | 'sms' | 'combined';
  recipientCount: number;
  dateCreated: string;
  dateSent?: string;
  status: 'sent' | 'draft' | 'scheduled';
}

const mockMails: MailItem[] = [
  {
    id: '1',
    title: 'Welcome to Mathematics Advanced Course',
    senderName: 'VIScool Admin',
    messagePreview: 'Welcome to our Mathematics Advanced course! We are excited to have you...',
    type: 'mail',
    recipientCount: 207,
    dateCreated: '2024-02-15',
    dateSent: '2024-02-15',
    status: 'sent',
  },
  {
    id: '2',
    title: 'Reminder: Physics Lab Session Tomorrow',
    senderName: 'VIScool Admin',
    messagePreview: 'This is a reminder that your Physics lab session is scheduled for tomorrow...',
    type: 'combined',
    recipientCount: 45,
    dateCreated: '2024-02-14',
    dateSent: '2024-02-14',
    status: 'sent',
  },
  {
    id: '3',
    title: 'Payment Due Reminder',
    senderName: 'Finance Team',
    messagePreview: 'Your payment for the current semester is due. Please complete your payment...',
    type: 'sms',
    recipientCount: 32,
    dateCreated: '2024-02-13',
    status: 'scheduled',
  },
  {
    id: '4',
    title: 'New Course Announcement',
    senderName: 'VIScool Admin',
    messagePreview: 'We are excited to announce a new Chemistry course starting next month...',
    type: 'mail',
    recipientCount: 0,
    dateCreated: '2024-02-12',
    status: 'draft',
  },
];

const typeLabels: Record<MailItem['type'], string> = {
  mail: 'Email',
  sms: 'SMS',
  combined: 'Email + SMS',
};

const typeColors: Record<MailItem['type'], string> = {
  mail: 'bg-blue-100 text-blue-700',
  sms: 'bg-green-100 text-green-700',
  combined: 'bg-purple-100 text-purple-700',
};

const statusColors: Record<MailItem['status'], string> = {
  sent: 'bg-green-100 text-green-700',
  draft: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-yellow-100 text-yellow-700',
};

export function MailingTab() {
  const [mails, setMails] = useState<MailItem[]>(mockMails);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'sent' | 'drafts'>('all');
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);

  const filteredMails = mails.filter(mail => {
    const matchesSearch = mail.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'sent' && mail.status === 'sent') ||
      (filter === 'drafts' && mail.status === 'draft');
    return matchesSearch && matchesFilter;
  });

  const handleDuplicate = (mail: MailItem) => {
    const newMail: MailItem = {
      ...mail,
      id: Date.now().toString(),
      title: `${mail.title} (Copy)`,
      status: 'draft',
      dateSent: undefined,
      dateCreated: new Date().toISOString().split('T')[0],
      recipientCount: 0,
    };
    setMails([newMail, ...mails]);
    setContextMenuId(null);
  };

  const handleDelete = (mailId: string) => {
    if (confirm('Are you sure you want to delete this mail?')) {
      setMails(mails.filter(m => m.id !== mailId));
    }
    setContextMenuId(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Sent Mails</h2>
          <span className="px-2 py-0.5 text-sm bg-gray-100 text-gray-600 rounded-full">
            {filteredMails.length}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            New Report
          </button>
          <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Share
          </button>
          <button
            onClick={() => setShowComposeModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Send New Mail
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Filter Tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {(['all', 'sent', 'drafts'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === f
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search mails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Mail List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="divide-y divide-gray-200">
          {filteredMails.map((mail) => (
            <div
              key={mail.id}
              className="flex items-center gap-4 p-4 hover:bg-gray-50 cursor-pointer"
            >
              {/* Mail Icon */}
              <div className={`p-2 rounded-lg ${mail.type === 'sms' ? 'bg-green-100' : 'bg-blue-100'}`}>
                {mail.type === 'sms' ? (
                  <MessageSquare className={`w-5 h-5 ${mail.type === 'sms' ? 'text-green-600' : 'text-blue-600'}`} />
                ) : (
                  <Mail className="w-5 h-5 text-blue-600" />
                )}
              </div>

              {/* Mail Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-gray-900 truncate">{mail.title}</h4>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${typeColors[mail.type]}`}>
                    {typeLabels[mail.type]}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[mail.status]}`}>
                    {mail.status.charAt(0).toUpperCase() + mail.status.slice(1)}
                  </span>
                </div>
                <p className="text-sm text-gray-500 truncate">{mail.messagePreview}</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                  <span>From: {mail.senderName}</span>
                  <span>Created: {new Date(mail.dateCreated).toLocaleDateString()}</span>
                  {mail.dateSent && <span>Sent: {new Date(mail.dateSent).toLocaleDateString()}</span>}
                </div>
              </div>

              {/* Recipients Counter */}
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">{mail.recipientCount}</div>
                  <div className="text-xs text-gray-500">Recipients</div>
                </div>

                {/* Quick Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Quick view"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicate(mail);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  {mail.status === 'sent' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                      title="Resend"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* More Actions */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setContextMenuId(contextMenuId === mail.id ? null : mail.id);
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {contextMenuId === mail.id && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuId(null);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Eye className="w-4 h-4" />
                        See Mail
                      </button>
                      {mail.status === 'sent' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setContextMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Send className="w-4 h-4" />
                          Resend
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicate(mail);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Copy className="w-4 h-4" />
                        Duplicate
                      </button>
                      <hr className="my-1" />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(mail.id);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredMails.length === 0 && (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No mails found</p>
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <ComposeMailModal onClose={() => setShowComposeModal(false)} />
      )}
    </div>
  );
}

function ComposeMailModal({ onClose }: { onClose: () => void }) {
  const [mailType, setMailType] = useState<'mail' | 'sms' | 'combined'>('mail');
  const [title, setTitle] = useState('');
  const [from, setFrom] = useState('admin@viscool.com');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState<string[]>([]);

  const characterLimit = mailType === 'sms' ? 160 : 10000;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 bg-black/50">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Create/Edit Mails</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Mail Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Type
              </label>
              <div className="flex gap-2">
                {(['mail', 'sms', 'combined'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setMailType(type)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                      mailType === type
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {typeLabels[type]}
                  </button>
                ))}
              </div>
            </div>

            {/* Mail Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mail Title / Subject
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter subject line..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {/* From */}
            {mailType !== 'sms' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mail From
                </label>
                <select
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="admin@viscool.com">admin@viscool.com</option>
                  <option value="noreply@viscool.com">noreply@viscool.com</option>
                  <option value="finance@viscool.com">finance@viscool.com</option>
                </select>
              </div>
            )}

            {/* Mail Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mail Body
              </label>
              {mailType !== 'sms' && (
                <div className="flex items-center gap-1 p-2 border border-gray-300 border-b-0 rounded-t-lg bg-gray-50">
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Bold className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Italic className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Underline className="w-4 h-4" />
                  </button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <AlignLeft className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <List className="w-4 h-4" />
                  </button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Link className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Image className="w-4 h-4" />
                  </button>
                  <button className="p-1.5 text-gray-600 hover:bg-gray-200 rounded">
                    <Smile className="w-4 h-4" />
                  </button>
                </div>
              )}
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, characterLimit))}
                placeholder="Write your message here..."
                rows={6}
                className={`w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                  mailType !== 'sms' ? 'rounded-b-lg' : 'rounded-lg'
                }`}
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${body.length >= characterLimit * 0.9 ? 'text-red-500' : 'text-gray-400'}`}>
                  {body.length}/{characterLimit}
                </span>
              </div>
            </div>

            {/* Send To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send To (Recipients)
              </label>
              <select
                multiple
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 min-h-[100px]"
              >
                <option value="all">Everyone</option>
                <option value="math">Mathematics Advanced Students</option>
                <option value="physics">Physics Bundle Students</option>
                <option value="english">English Literature Students</option>
                <option value="pending">Students with Pending Payments</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl/Cmd to select multiple recipients
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Save as Draft
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button className="px-6 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center gap-2">
                <Send className="w-4 h-4" />
                Send Mail
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
